// 리포트 v2 실응답 연결
//
// 계약: sales-server/docs/report-v2/report-v2-api-contract.md (read-only)

/**
 * v2 경로 사용 여부 — 리뉴얼 배포 형태가 v2 고정이라 true 로 박아 둔다.
 * 출시 정리(mock 제거) 때 이 상수와 아래 참조 지점들을 함께 걷어낸다.
 */
export const IS_REPORT_V2_TEST = true;

/** 운영이 보내는 리포트 API version (기존 동작 — 절대 변경 금지) */
export const REPORT_API_VERSION = "2026.5.27";

/**
 * v2 게이트를 통과시키는 version.
 * 계약 §13 의 `V2_MIN_VERSION` placeholder(2026.9.1) 기준 — 서버 세션이 릴리즈 버전을
 * 확정하면 이 상수만 바꾼다. (suspect: 확정값 회신 대기)
 */
export const REPORT_V2_TEST_VERSION = "2026.9.1";

/** v2 응답이 오는 item 목록 */
// 서버 배포 커밋 411eb967 (report_v2.py:32) — 8탭 전원 등재 [우진 전달 확정 2026-08-07].
// 계약 §1-1 의 "4탭 한정" 조항은 이 배포로 개정됐다.
export const V2_ITEMS = [
  "salesSummary",
  "taxRefund",
  "delivery",
  "storeList",
  "product",
  "discount",
  "customer",
  "membership",
];

/**
 * 이 item 을 v2 로 다룰지 — Phase 1 범위 밖 item 은 v1 version 으로 나간다.
 * @param {string} item
 */
export const isReportV2 = (item) =>
  IS_REPORT_V2_TEST && V2_ITEMS.includes(item);

// 계약 §1-0 [확정]: v2 신규 인자는 전부 `options: AWSJSON` 내부 키다.
// 서버가 화이트리스트 검증하며 미지 키는 명시 에러 → 아래 목록 외 키를 넣지 마라.
// locationIds 는 제거됐다 (§1-0, 매장 다중선택 미확정).
const OPTION_KEYS = [
  "compare", // "prevPeriod"(기본) | "prevYear" | "none" | "custom"
  "compareStart",
  "compareEnd",
  "mode", // 재조회형 전환 (salesSummary.table · delivery.table · product)
  "grain", // membership.growth
  "dimension", // product.rank · customer.productRank
  "page",
  "size",
  "sort", // "paidAmount:desc"
  "q",
  "couponId",
  // churnBand 삭제 [2026-08-12] — 서버가 churnList 를 폐지하고 밴드별 숫자만 준다.
  // 밴드 선택 재조회 자체가 사라졌다 (이탈 위험 고객 = 카드 3장 + 태그 생성).
  "detail", // 추출 전용: "product" | "sku"
  "categoryIds", // product.table 카테고리 필터 (§2-2 ③) — 서버 화이트리스트 등재 대기 S-9
  "sections", // S-40 분리 로딩 (include-list) — SECTION_SPLIT 참조
];

/**
 * 추출(§12) 전용 options 화이트리스트 — 서버 `_EXPORT_OPTIONS_KEYS` 와 같은 집합.
 *
 * 🔴 조회용(OPTION_KEYS)과 **다르다** [S-53 ①]: 조회는 locationId·basis·origin·
 *    brandId 를 **스키마 인자**로 받지만, 추출은 그 인자가 없어 **options 로만**
 *    받는다. 조회용으로 거르면 지점 필터가 통째로 탈락해 다운로드가 전체지점으로
 *    나간다(우진 실기기). page/size 는 계약상 금지(§12-1 항상 전량).
 */
const EXPORT_OPTION_KEYS = [
  "scope",
  "compare",
  "compareStart",
  "compareEnd",
  "basis",
  "origin",
  "locationId",
  "brandId",
  "couponId",
  "sort",
  "q",
  "detail",
  "mode",
  "grain",
  "dimension",
  "categoryIds",
  // 화면 컬럼 숨김을 엑셀에도 반영 [서버 계약 2026-08-18]
  "excludeHeaderKeys",
];

/**
 * 추출 options 조립 — 빈 값은 싣지 않는다.
 * @param {object} input
 * @returns {object|null}
 */
export const buildExportOptions = (input) => {
  if (!input) return null;
  const options = EXPORT_OPTION_KEYS.reduce((acc, key) => {
    const value = input[key];
    if (value !== undefined && value !== null && value !== "") acc[key] = value;
    return acc;
  }, {});
  return Object.keys(options).length ? options : null;
};

/**
 * S-40 섹션 분리 로딩 [서버 통지 2026-08-13].
 *
 * 무거운 섹션 때문에 탭 전체가 10s 가드에 걸려 「조회 실패」로 보이던 것을 없앤다
 * [우진 원칙: "가드로 포기하지 말고 분리 로딩. 30s 하드 실패만 불가항력"].
 * 1차 = 가벼운 섹션(즉시 렌더) → 2차 = 무거운 섹션(자체 로딩 UI).
 *
 * 🔴 `sections` 키 자체는 서버 화이트리스트에 등재돼 있어(배포본 확인) 보내도
 *    에러는 안 난다. 다만 **서버가 그 item 에서 sections 를 실제로 해석해야**
 *    분리가 성립한다 — 미지원 item 에 보내면 서버가 무시하고 전량을 두 번 주므로
 *    비용만 2배다. 그래서 item 별 게이트를 둔다.
 *    · discount = 배포됨(`sections=sections` 전달 확인, 배포본 86ed3bf2)
 *    · 3 item 모두 배포 확인 완료 [2026-08-14 — 배포본 320e5677 에서
 *      discount/product/customer 전부 `sections=sections` 전달]
 */
export const SECTION_SPLIT = {
  discount: {
    enabled: true,
    // 2차로 미루는 무거운 섹션 (ISHF 집계 2.6~4.5s)
    deferred: ["topCoupons"],
    // 1차에 받을 섹션 — 서버 _FOLD_SECS 와 같은 이름
    first: ["summary", "breakdown", "daily", "byLocation", "usage"],
  },
  product: {
    // 서버 4차 배포 확인 [2026-08-14 — 커밋 38bc9df5 ⊂ 배포본 320e5677,
    // `_assemble_product(..., sections=sections)` 전달 확인]
    enabled: true,
    deferred: ["timeTop"],
    // 🔴 이름은 **서버 data 키**다 — 화면 이름을 쓰면 include-list 에서 빠져
    //    서버가 그 섹션 계산을 통째로 생략한다(빈 화면인데 경고도 없다)
    //    [우진 QA 2026-08-14 — "highlight" 오기로 movers·refundRisk·lowSales 누락].
    //    render-smoke 가 서버 소스와 키 집합을 자동 대조한다.
    first: [
      // mode = 탭 에코(§6) — 데이터 키라 목록에 없으면 필터로 잘려, 파서가
      // 카테고리/사이드메뉴 응답을 "product" 로 오판한다(가드가 잡아낸 2호)
      "mode",
      "kpi",
      "table",
      "movers",
      "refundRisk",
      "lowSales",
      "categoryShare",
      "newProducts",
      "bestCompare",
      "productCustomer",
    ],
  },
  customer: {
    // 서버 4차 배포 확인 [2026-08-14 — 동일 커밋, `_assemble_customer(...,
    // sections=sections)` 전달 확인]. 무거운 섹션을 뒤로 미뤄 고객 탭
    // 전면 실패(30s 초과)를 없앤다
    enabled: true,
    // 🔴 2차를 **kpi 단독**으로 분할 [S-50 우진 확정 2026-08-14]. 서버 실측:
    //    2차 통짜 ~10.8s 의 정체는 kpi(1.3s)가 아니라 동승 섹션이었다
    //    (productRank 5.5s · cohort 2.8s · repurchase 0.6 · demographic 0.6).
    //    kpi 를 떼면 **최상단 카드가 1.3s 에 뜬다** — 나머지는 3차로 미룬다.
    stages: [
      ["kpi"],
      ["cohort", "repurchaseTrend", "byDemographic", "productRank"],
    ],
    // stages 의 평탄화 — 1차 시점 pending 표시·키 대조용 (단계 구분 없음)
    deferred: ["kpi", "cohort", "repurchaseTrend", "byDemographic", "productRank"],
    first: [
      "byChannel",
      "bySegment",
      "visitByWeekday",
      "visitByBucket",
      "visitTrend",
      "locationConversion",
      // 🔴 서버 키 = locationSegmentSales (화면명 storeTypeAmount 아님) —
      //    오기로 1차 목록에서 빠져 「매장별 고객유형 구매금액」이 항상 비었다
      //    [우진 QA 2026-08-14 실화면]
      "locationSegmentSales",
    ],
  },
};

/** 이 item 을 단계로 나눠 부를 것인가 */
export const canSplitSections = (item) => !!SECTION_SPLIT[item]?.enabled;

/**
 * 1차 이후 **순차 호출할 단계 목록**. stages 가 없으면 deferred 한 덩어리다.
 * @param {object} split - SECTION_SPLIT[item]
 * @returns {Array<Array<string>>}
 */
export const splitStagesOf = (split) =>
  split ? split.stages || [split.deferred] : [];

/**
 * product.table 카테고리 필터(options.categoryIds) 서버 지원 여부 [S-9 대기].
 * 🔴 서버 화이트리스트에 없는 키를 보내면 **명시 에러로 조회 전체가 깨진다** (§1-0) —
 * 서버가 회신하면 이 상수만 true 로 바꾼다. false 인 동안 웹은 categoryIds 를 안 싣는다.
 * (표가 서버 페이지드 ·전량 450행이라 클라 필터는 페이지 좌표가 깨져 금지 — 서버 WHERE 가 정답)
 */
// [배포 확인 2026-08-11] 서버 커밋 823b2e9f(fix w8) ⊂ 배포본 1275e6f7 — categoryIds
// 화이트리스트·WHERE 필터 라이브. 클라 필터는 서버 페이지드 표에서 **현재 페이지만**
// 걸러 다른 페이지의 해당 카테고리 제품이 사라졌다 [우진 실화면: 플랫슈즈 0건].
export const PRODUCT_CATEGORY_FILTER_READY = true;

/**
 * 배달 주문 리포트 표의 **일자 × 매장 교차 모드**(S-32) 지원 여부.
 * 🔴 서버는 `mode` 허용값 밖이면 **명시 에러로 조회 전체를 실패**시킨다
 *    (report_v2.py `_assemble_delivery`: location|daily 만 허용) — 미배포 상태에서
 *    보내면 배달 탭이 통째로 안 나온다. 배포 통지가 오면 이 상수만 true 로 바꾼다.
 * true 가 되면: 본사 표가 탭 없이 「일자·지점」 한 행(구 리포트 동형)으로 전환된다.
 *
 * [배포 확인 2026-08-11] 서버 커밋 `66f51099`(w20) ⊂ master — `_assemble_delivery` 가
 * mode "dailyByLocation" 을 허용하고 행에 locationName·플랫폼 3열을 싣는다. → ON.
 */
export const DELIVERY_DAILY_BY_LOCATION_READY = true;

/**
 * 백필(fact) 계정 여부 — 서버가 meta.source.fact 로 알려 준다 (§1-0-2).
 * 🔴 fact 계정의 스냅샷/장기창 계열(코호트·재구매 시계열·14일+ 데모·고객 KPI·
 *    멤버십 등급/신규)은 **어제까지 집계 기준**이라 화면에 그 표기를 단다
 *    [우진 확정 2026-08-12 — 표기 3건 ②]. live 계정은 오늘까지 실시간이라 미표기.
 */
export const isFactSource = (meta) => meta?.source?.fact === true;

/** fact 계정 스냅샷 계열 캡션 (없으면 null → 미표기) */
export const asOfYesterdayCaption = (meta) =>
  isFactSource(meta) ? "어제까지 집계 기준" : null;

/**
 * fact 창 하한(서버 FACT_MIN_START_KST 미러) [표기 3건 ④ — 우진 제안 2026-08-12].
 * 화이트리스트/EAGER 계정도 이 날짜 **이전** 기간은 fact 미적용(legacy/live)이라
 * 느릴 수 있다 — 기간 피커가 소프트 안내를 띄운다(차단 아님, 조회는 동작).
 * live 계정은 전 기간 동일 경로라 해당 없음.
 */
export const FACT_WINDOW_FROM = "2025-01-01";
export const DELIVERY_DAILY_BY_LOCATION_MODE = "dailyByLocation";

/**
 * v2 요청 options 조립 — 화이트리스트 키만, 빈 값은 제외.
 * 직렬화(JSON.stringify)는 API 계층 한 곳에서 한다 (AWSJSON).
 * @param {object} [input]
 * @returns {object|null} 보낼 키가 없으면 null
 */
export const buildReportOptions = (input) => {
  if (!input) return null;
  // `__sectionRetry` 는 **웹 내부 표식**이다 — OPTION_KEYS 밖이라 서버로 나가지
  // 않는다(§1-0: 미지 키는 명시 에러). 사가·리듀서만 읽는다.
  const options = OPTION_KEYS.reduce((acc, key) => {
    const value = input[key];
    if (value !== undefined && value !== null && value !== "") acc[key] = value;
    return acc;
  }, {});
  return Object.keys(options).length ? options : null;
};

/**
 * v2 공통 봉투(§2) 언랩 — data 만 꺼내되 meta·warnings 는 버리지 않는다.
 * meta.page 는 서버 페이지네이션 표(§11-1)의 입력이고,
 * warnings 는 COMPARE_NOT_READY 등 상태 처리(§2-3)의 입력이다.
 * @param {object} envelope - getPeriodReport 응답
 * @returns {{data:object, meta:object, warnings:Array}|null}
 */
export const unwrapReportEnvelope = (envelope) => {
  if (!isV2Envelope(envelope)) return null;
  return {
    data: envelope.data,
    meta: envelope.meta || {},
    warnings: envelope.warnings || [],
    // S-40: 2차 응답을 기다리는 섹션 (사가가 1차 봉투에 심는다). 뷰는 이 목록에
    // 있는 섹션을 「데이터 없음」이 아니라 **로딩**으로 그린다
    pendingSections: envelope.pendingSections || [],
  };
};

/**
 * 이 섹션이 2차 응답 대기 중인가 (S-40 분리 로딩).
 * @param {object} parsed - 파서가 보관한 뷰모델 (pendingSections 포함)
 * @param {string} sectionKey
 */
export const isSectionPending = (parsed, sectionKey) =>
  (parsed?.pendingSections || []).includes(sectionKey);

/**
 * v2 봉투인지 (data 키 보유). v1 응답은 stats/daily/… 를 top-level 에 둔다.
 * @param {*} payload
 */
export const isV2Envelope = (payload) =>
  !!payload && typeof payload === "object" && !!payload.data;

/** 응답이 비어 있는가 (아직 도착 전 — 폴백 경고 대상 아님) */
const isEmptyPayload = (payload) =>
  !payload ||
  (typeof payload === "object" && Object.keys(payload).length === 0);

// item 별 1회만 경고 (렌더마다 콘솔이 도배되지 않게)
const warned = new Set();

/**
 * 🔴 오판 방지 (게이트④): 스위치 on 인데 v2 봉투가 아닌 응답이 오면
 * 조용히 폴백하지 않고 알린다. 테스트 version 이 게이트를 못 넘었거나
 * 서버가 해당 item 을 아직 v2 로 안 내리는 상황을 놓치면 검증 결과 전체가 오판된다.
 * @param {*} payload - 응답
 * @param {string} item
 * @returns {boolean} true 면 v1/mock 폴백 중 (화면은 디버그 배지를 띄운다)
 */
export const isV2Fallback = (payload, item) => {
  if (!IS_REPORT_V2_TEST) return false;
  if (isEmptyPayload(payload)) return false; // 아직 응답 전
  if (isV2Envelope(payload)) return false;
  if (!warned.has(item)) {
    warned.add(item);
    // eslint-disable-next-line no-console
    console.warn(
      `[report-v2] "${item}" 응답이 v2 봉투가 아니다 — v1/mock 으로 폴백 중.` +
        ` version="${REPORT_V2_TEST_VERSION}" 이 서버 게이트를 넘었는지,` +
        ` 해당 item 이 V2_ITEMS 게이트에 포함됐는지 확인하라.`,
      payload,
    );
  }
  return true;
};
